import { Tabs as DSTabs } from '@digdir/designsystemet-react'
import { useEffect, useRef, type ComponentRef } from 'react'
import { useIsPhone } from '@/shared/hooks/useMediaQuery'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TabItem {
  key: string
  label: string
  /** Optional count badge displayed next to the label */
  count?: number
}

export interface TabsProps {
  tabs: TabItem[]
  /** Key of the currently active tab */
  active: string
  /** Called with the tab key when the user selects a tab */
  onChange: (key: string) => void
  /**
   * Opt in to the full tab/panel ARIA wiring. Every tab gets
   * `id="<prefix>-tab-<key>"`, and the selected one points at
   * `<prefix>-panel-<key>` — the id the owner puts on its `role="tabpanel"`.
   * Only the selected tab carries aria-controls: an owner that renders one
   * panel at a time has no element behind the other ids.
   */
  idPrefix?: string
  /** Translated accessible name for the tab list. */
  ariaLabel?: string
  /**
   * `page` re-seats the strip inside a padded page body (it sheds the shell's
   * horizontal inset and wraps on a narrow viewport) instead of running flush
   * against the app shell.
   * `sub` uses the pill sub-navigation appearance shared by SubTabs.
   */
  variant?: 'shell' | 'page' | 'sub'
}

// ---------------------------------------------------------------------------
// Tabs
//
// Underline page-tab navigation strip. Presentational: all state (which tab is
// active) is owner-controlled via `active` + `onChange`. Safe for the
// sessionless public register bundle.
//
// Keyboard pattern (ARIA authoring practices — manual activation):
//   ArrowRight / ArrowLeft — move focus (wrapping), does NOT auto-activate
//   Home / End              — move focus to the first / last tab
//   Enter / Space           — activate the focused tab
//
// The active tab has tabIndex=0 (roving tabindex). All others have -1.
// ---------------------------------------------------------------------------

export function Tabs({ tabs, active, onChange, idPrefix, ariaLabel, variant = 'shell' }: TabsProps) {
  const root = useRef<ComponentRef<typeof DSTabs>>(null)
  const isPhone = useIsPhone()
  useEffect(() => {
    const list = root.current?.querySelector<HTMLElement>('[role="tablist"]')
    const selected = list?.querySelector<HTMLElement>('[aria-selected="true"]')
    if (!list || !selected) return
    const bounds = list.getBoundingClientRect(), tab = selected.getBoundingClientRect()
    if (tab.left < bounds.left) list.scrollLeft += tab.left - bounds.left
    else if (tab.right > bounds.right) list.scrollLeft += tab.right - bounds.right
  }, [active, isPhone])
  const tabClass = variant === 'sub' ? 'subtab' : 'tab'
  return (
    <DSTabs ref={root} value={active} className={`tabs-root tabs-root--${variant}`}>
      <DSTabs.List
        className={variant === 'sub' ? 'subtab-bar' : `tabs${variant === 'page' ? ' tabs--page' : ''}`}
        aria-label={ariaLabel}
      >
        {tabs.map((tab) => {
          const isActive = tab.key === active
          return (
            <button
              key={tab.key}
              role="tab"
              type="button"
              id={idPrefix ? `${idPrefix}-tab-${tab.key}` : undefined}
              aria-controls={idPrefix && isActive ? `${idPrefix}-panel-${tab.key}` : undefined}
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              className={`${tabClass}${isActive ? ` ${tabClass}--active` : ''}`}
              // DS.Tab invents controls for absent panels and selects on connection.
              // Native buttons keep React's ARIA state; DS.List owns all keyboard handling.
              // Cancel its click default before the native list can change selection.
              onClickCapture={(event) => event.preventDefault()}
              onClick={() => onChange(tab.key)}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="tab__count">{tab.count}</span>
              )}
            </button>
          )
        })}
      </DSTabs.List>
    </DSTabs>
  )
}
